import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, FormControlLabel, IconButton, MenuItem, Stack, Switch,
  TextField, Tooltip, Typography,
} from "@mui/material";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import {
  useGetNumbersQuery, useCreateNumberMutation, useDeleteNumberMutation,
  useTestNumberSmsMutation,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, FilterBar, SearchBox, DataTable, StatusBadge,
  ActionButtons, ActionButton, SlidePanel, ConfirmDialog, type DataTableColumn,
} from "../../../Components/ui";
import useNotifier from "../../../Core/Notifier/use-notifier";
import { NAVY } from "../../../theme/tokens";

const SMS_PROVIDERS = ["telnyx", "twilio", "plivo"];
const VOICE_PROVIDERS = ["3cx", "telnyx", "twilio"];

const empty = {
  number: "", country_code: "+1", sms_enabled: true, voice_enabled: false,
  sms_provider: "telnyx", voice_provider: "3cx", status: "active",
};

const Numbers: React.FC = () => {
  const { data, isLoading } = useGetNumbersQuery();
  const [createNumber] = useCreateNumberMutation();
  const [deleteNumber] = useDeleteNumberMutation();

  const [testSms, { isLoading: sendingTest }] = useTestNumberSmsMutation();
  const { showMessage, showErrorMessage } = useNotifier();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(empty);
  const [search, setSearch] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);

  // Test SMS modal state.
  const [testNum, setTestNum] = useState<any | null>(null);
  const [testForm, setTestForm] = useState({ to: "", message: "Testing INTALK SMS delivery" });
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string; sub?: string } | null>(null);

  const openTest = (n: any) => {
    setTestNum(n);
    setTestForm({ to: "", message: "Testing INTALK SMS delivery" });
    setTestResult(null);
  };
  const sendTest = async () => {
    if (!testNum) return;
    setTestResult(null);
    try {
      const res: any = await testSms({ id: testNum.id, body: testForm }).unwrap();
      const sub = `Message ID: ${res.data?.provider_message_id ?? "—"} · Status: ${res.data?.provider_status ?? "—"}`;
      setTestResult({ ok: true, msg: res.message || "SMS sent successfully", sub });
      showMessage("SMS sent successfully", sub);
    } catch (e: any) {
      const msg = e?.data?.message || "Failed to send test SMS";
      setTestResult({ ok: false, msg });
      showErrorMessage(msg);
    }
  };

  const rows = data?.data ?? [];
  const set = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.checked });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((n: any) =>
      [n.number, n.sms_provider, n.voice_provider, n.country_code]
        .filter(Boolean).some((v: string) => v.toLowerCase().includes(q)));
  }, [rows, search]);

  const save = async () => { await createNumber(form).unwrap(); setOpen(false); setForm(empty); };

  const columns: DataTableColumn<any>[] = [
    {
      id: "number", label: "Phone Number",
      render: (n) => (
        <Box>
          <Typography
            fontSize={15}
            fontWeight={700}
            color={NAVY}
            sx={{ fontFamily: "monospace", letterSpacing: "0.05em" }}
          >
            {n.number}
          </Typography>
          {n.country_code && (
            <Typography fontSize={12} color="text.secondary">{n.country_code}</Typography>
          )}
        </Box>
      ),
    },
    {
      id: "sms", label: "SMS",
      render: (n) => n.sms_enabled
        ? (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <StatusBadge status="active" label="SMS" />
            {n.sms_provider && (
              <Typography fontSize={11} color="text.secondary" sx={{ fontStyle: "italic" }}>
                {n.sms_provider}
              </Typography>
            )}
          </Stack>
        )
        : <StatusBadge status="inactive" label="Off" />,
    },
    {
      id: "voice", label: "Voice",
      render: (n) => n.voice_enabled
        ? (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <StatusBadge status="active" label="Voice" tone="info" />
            {n.voice_provider && (
              <Typography fontSize={11} color="text.secondary" sx={{ fontStyle: "italic" }}>
                {n.voice_provider}
              </Typography>
            )}
          </Stack>
        )
        : <StatusBadge status="inactive" label="Off" />,
    },
    { id: "status", label: "Status", render: (n) => <StatusBadge status={n.status} /> },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<PhoneRoundedIcon />}
          title="Phone Numbers"
          subtitle="Manage SMS, voice, and messaging numbers on the platform"
          actions={
            <ActionButton startIcon={<AddRoundedIcon />} onClick={() => { setForm(empty); setOpen(true); }}>
              Add Number
            </ActionButton>
          }
        />
      }
    >
      <FilterBar right={<Typography fontSize={13} color="text.secondary">{filtered.length} numbers</Typography>}>
        <SearchBox value={search} onChange={setSearch} placeholder="Search numbers…" />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        actions={(n) => (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Tooltip title={n.sms_enabled ? "Test SMS" : "Enable SMS to test"} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={!n.sms_enabled}
                  onClick={() => openTest(n)}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#058dd0",
                    "&:hover": { backgroundColor: "#E3F2FB" },
                  }}
                >
                  <SmsRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <ActionButtons onDelete={() => setConfirmId(n.id)} />
          </Stack>
        )}
        empty={{
          icon: <PhoneRoundedIcon />,
          title: "No phone numbers yet",
          message: "Add a number to enable SMS, voice, and messaging capabilities.",
          action: <ActionButton startIcon={<AddRoundedIcon />} onClick={() => { setForm(empty); setOpen(true); }}>Add Number</ActionButton>,
        }}
      />

      <SlidePanel
        open={open}
        onClose={() => setOpen(false)}
        title="Add Number"
        subtitle="Register a phone number for SMS or voice"
        icon={<PhoneRoundedIcon />}
        footer={
          <>
            <Button variant="outlined" onClick={() => setOpen(false)} sx={{ borderRadius: "10px" }}>Cancel</Button>
            <ActionButton onClick={save} disabled={!form.number}>
              Save Number
            </ActionButton>
          </>
        }
      >
        <Stack spacing={2}>
          <TextField
            label="Number (E.164, e.g. +19057985515)"
            value={form.number}
            onChange={set("number")}
            fullWidth
            required
            inputProps={{ style: { fontFamily: "monospace", letterSpacing: "0.05em" } }}
          />
          <TextField
            label="Country Code"
            value={form.country_code}
            onChange={set("country_code")}
            sx={{ width: 140 }}
          />
          <FormControlLabel
            control={<Switch checked={form.sms_enabled} onChange={toggle("sms_enabled")} />}
            label="SMS enabled"
          />
          {form.sms_enabled && (
            <TextField select label="SMS Provider" value={form.sms_provider} onChange={set("sms_provider")} fullWidth>
              {SMS_PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          )}
          <FormControlLabel
            control={<Switch checked={form.voice_enabled} onChange={toggle("voice_enabled")} />}
            label="Voice enabled"
          />
          {form.voice_enabled && (
            <TextField select label="Voice Provider" value={form.voice_provider} onChange={set("voice_provider")} fullWidth>
              {VOICE_PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          )}
        </Stack>
      </SlidePanel>

      <SlidePanel
        open={testNum !== null}
        onClose={() => setTestNum(null)}
        title="Send Test SMS"
        subtitle={testNum ? `From ${testNum.number}` : ""}
        icon={<SmsRoundedIcon />}
        footer={
          <>
            <Button variant="outlined" onClick={() => setTestNum(null)} sx={{ borderRadius: "10px" }}>Cancel</Button>
            <ActionButton
              startIcon={<SmsRoundedIcon />}
              onClick={sendTest}
              disabled={!testForm.to.trim() || !testForm.message.trim() || sendingTest}
            >
              {sendingTest ? "Sending…" : "Send Test SMS"}
            </ActionButton>
          </>
        }
      >
        <Stack spacing={2}>
          {testResult && (
            <Alert severity={testResult.ok ? "success" : "error"}>
              <Typography fontSize={14} fontWeight={600}>{testResult.msg}</Typography>
              {testResult.sub && <Typography fontSize={12} sx={{ mt: 0.5 }}>{testResult.sub}</Typography>}
            </Alert>
          )}
          <TextField
            label="From Number"
            value={testNum?.number ?? ""}
            fullWidth
            InputProps={{ readOnly: true, style: { fontFamily: "monospace", letterSpacing: "0.05em" } }}
          />
          <TextField
            label="Send To (E.164, e.g. +16475551234)"
            value={testForm.to}
            onChange={(e) => setTestForm({ ...testForm, to: e.target.value })}
            fullWidth
            required
            inputProps={{ style: { fontFamily: "monospace", letterSpacing: "0.05em" } }}
          />
          <TextField
            label="Message"
            value={testForm.message}
            onChange={(e) => setTestForm({ ...testForm, message: e.target.value })}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
      </SlidePanel>

      <ConfirmDialog
        open={confirmId !== null}
        title="Delete number?"
        message="This permanently removes the phone number and its configuration. This cannot be undone."
        confirmText="Delete"
        danger
        onClose={() => setConfirmId(null)}
        onConfirm={async () => { if (confirmId !== null) await deleteNumber(confirmId); setConfirmId(null); }}
      />
    </AppPage>
  );
};

export default Numbers;
